import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Wallet, DollarSign, CreditCard, Coins, MoreHorizontal } from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { api, Wallet as WalletType } from '../../lib/api';
import { isCashierEntityAllowed } from './constants';

interface WalletBalanceCardsProps {
  refreshKey?: number;
}

interface WalletWithBalance {
  id: string;
  name: string;
  type: string;
  currencyCode: string;
  balance: number;
}

const getWalletIcon = (type: string, currencyCode: string) => {
  const isCrypto = ['USDT', 'BTC', 'ETH', 'USDC', 'BNB', 'SOL', 'XRP'].includes(currencyCode.toUpperCase());

  if (isCrypto) {
    return <Coins size={18} />;
  }

  switch (type?.toLowerCase()) {
    case 'cash':
      return <Wallet size={18} />;
    case 'bank':
      return <CreditCard size={18} />;
    default:
      return <DollarSign size={18} />;
  }
};

const getIconBgColor = (currencyCode: string, index: number) => {
  const colors = [
    'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', // Blue
    'linear-gradient(135deg, #10b981 0%, #059669 100%)', // Green
    'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', // Yellow
    'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', // Purple
  ];

  // Crypto currencies get green
  if (['USDT', 'USDC'].includes(currencyCode.toUpperCase())) {
    return colors[1];
  }
  if (['BTC', 'ETH'].includes(currencyCode.toUpperCase())) {
    return colors[3];
  }

  return colors[index % colors.length];
};

export function WalletBalanceCards({ refreshKey }: WalletBalanceCardsProps) {
  const { t } = useTranslation();
  const { currentEntity } = useEntity();
  const [wallets, setWallets] = useState<WalletWithBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!currentEntity?.id) return;

    // Only fetch wallets for FNK entity
    if (!isCashierEntityAllowed(currentEntity.code)) {
      setWallets([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch both wallets and balances
      const [walletsResult, balancesResult] = await Promise.all([
        api.admin.wallets.list(currentEntity.id),
        api.reports.balances(currentEntity.id),
      ]);

      // Get active wallets
      const activeWallets = walletsResult.data.filter((w: WalletType) => w.isActive);

      // Create a map of wallet ID to balance from the balances report (per-wallet, not aggregated)
      const walletBalanceMap: Record<string, number> = {};
      if (balancesResult?.byType) {
        // byType is grouped by wallet type (cash, bank, crypto, etc.)
        Object.values(balancesResult.byType).forEach((walletGroup: any) => {
          if (Array.isArray(walletGroup)) {
            walletGroup.forEach((wallet: { walletId: string; balance?: string | number }) => {
              const balance = wallet.balance || 0;
              walletBalanceMap[wallet.walletId] = typeof balance === 'string' ? parseFloat(balance) : balance;
            });
          }
        });
      }

      // Merge wallets with their individual balances
      const walletsWithBalance: WalletWithBalance[] = activeWallets
        .slice(0, 4) // Limit to 4 for display
        .map((w: WalletType) => ({
          id: w.id,
          name: w.name,
          type: w.type,
          currencyCode: w.currencyCode,
          balance: walletBalanceMap[w.id] || 0,
        }));

      setWallets(walletsWithBalance);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load wallets');
    } finally {
      setLoading(false);
    }
  }, [currentEntity?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const formatBalance = (balance: number) => {
    return balance.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (loading) {
    return (
      <div className="wallet-balance-cards">
        <div className="wallet-card loading">
          <span>{t('cashier.walletCards.loading', 'Loading wallets...')}</span>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wallet-balance-cards">
        <div className="wallet-card error">
          <span>{error}</span>
          <button onClick={fetchData} className="retry-btn">
            {t('cashier.walletCards.retry', 'Retry')}
          </button>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="wallet-balance-cards">
      {wallets.map((wallet, index) => (
        <div key={wallet.id} className="wallet-card">
          <div className="wallet-header">
            <div
              className="wallet-icon"
              style={{ background: getIconBgColor(wallet.currencyCode, index) }}
            >
              {getWalletIcon(wallet.type, wallet.currencyCode)}
            </div>
            <span className="wallet-name">{wallet.name}</span>
            <button className="more-btn">
              <MoreHorizontal size={16} />
            </button>
          </div>
          <div className="wallet-balance">
            {formatBalance(wallet.balance)}
          </div>
          <div className="wallet-currency">
            {wallet.currencyCode}
          </div>
        </div>
      ))}

      {wallets.length === 0 && (
        <div className="wallet-card empty">
          <span>{t('cashier.walletCards.noWallets', 'No wallets configured')}</span>
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .wallet-balance-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }

  .wallet-card {
    background: var(--color-surface);
    border-radius: 10px;
    padding: 14px;
    box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    border: 1px solid var(--color-border);
    position: relative;
    transition: box-shadow 0.2s ease;
  }

  .wallet-card:hover {
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  }

  .wallet-card.loading,
  .wallet-card.error,
  .wallet-card.empty {
    grid-column: span 4;
    text-align: center;
    padding: 24px;
    color: var(--color-text-muted);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .wallet-card.error {
    color: var(--ctp-red);
  }

  .retry-btn {
    padding: 6px 14px;
    background: var(--ctp-teal);
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
  }

  .retry-btn:hover {
    background: #0f766e;
  }

  .wallet-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
  }

  .wallet-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    flex-shrink: 0;
  }

  .wallet-name {
    font-size: 12px;
    font-weight: 500;
    color: var(--color-text-muted);
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .more-btn {
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    color: var(--color-text-muted);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background 0.15s, color 0.15s;
  }

  .more-btn:hover {
    background: var(--color-surface-hover);
    color: var(--color-text);
  }

  .wallet-balance {
    font-size: 20px;
    font-weight: 700;
    color: var(--color-text);
    font-family: 'Inter', -apple-system, sans-serif;
    margin-bottom: 2px;
    line-height: 1.2;
  }

  .wallet-currency {
    font-size: 11px;
    font-weight: 500;
    color: var(--color-text-muted);
    text-transform: uppercase;
  }

  /* Responsive */
  @media (max-width: 1200px) {
    .wallet-balance-cards {
      grid-template-columns: repeat(2, 1fr);
    }

    .wallet-card.loading,
    .wallet-card.error,
    .wallet-card.empty {
      grid-column: span 2;
    }
  }

  @media (max-width: 768px) {
    .wallet-balance-cards {
      grid-template-columns: 1fr;
    }

    .wallet-card.loading,
    .wallet-card.error,
    .wallet-card.empty {
      grid-column: span 1;
    }
  }
`;
